import { useState } from "react";

import { ApiClientError } from "../api/client.js";
import { formatMonthLabel } from "../lib/format.js";
import { useStore } from "../store.js";

const inputClass =
  "w-full rounded-lg border border-node-border bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-accent";

const CURRENCIES = [
  { code: "GBP", label: "GBP - British Pound" },
  { code: "EUR", label: "EUR - Euro" },
  { code: "USD", label: "USD - US Dollar" },
  { code: "AUD", label: "AUD - Australian Dollar" },
] as const;

interface Props {
  onClose: () => void;
}

export function LedgerSettings({ onClose }: Props) {
  const ledger = useStore((state) =>
    state.ledgers.find((entry) => entry.id === state.currentLedgerId),
  );
  const updateLedgerSettings = useStore((state) => state.updateLedgerSettings);

  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  if (!ledger) {
    return null;
  }

  async function save(patch: Parameters<typeof updateLedgerSettings>[0], success: string) {
    setMessage(null);
    try {
      await updateLedgerSettings(patch);
      setMessage({ kind: "ok", text: success });
    } catch (caught) {
      const text = caught instanceof ApiClientError ? caught.message : "Something went wrong.";
      setMessage({ kind: "error", text });
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-node-border bg-node-bg p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Ledger settings</h2>
          <button type="button" onClick={onClose} className="text-sm text-ink-muted hover:text-ink">
            Close
          </button>
        </div>

        <p className="mb-4 text-sm text-ink-muted">
          Settings for <span className="text-ink">{ledger.name}</span>.
        </p>

        {message ? (
          <p
            className={`mb-4 rounded-lg px-3 py-2 text-sm ${
              message.kind === "ok"
                ? "border border-positive/40 bg-positive/10 text-ink"
                : "border border-warning/40 bg-warning/10 text-ink"
            }`}
          >
            {message.text}
          </p>
        ) : null}

        <label className="mb-1 block text-xs text-ink-muted" htmlFor="ledger-currency">
          Default currency
        </label>
        <select
          id="ledger-currency"
          className={`${inputClass} mb-1`}
          value={ledger.currency}
          onChange={(event) => void save({ currency: event.target.value }, "Currency updated.")}
        >
          {CURRENCIES.some((entry) => entry.code === ledger.currency) ? null : (
            <option value={ledger.currency}>{ledger.currency}</option>
          )}
          {CURRENCIES.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.label}
            </option>
          ))}
        </select>
        <p className="mb-5 text-xs text-ink-muted">
          New nodes use this currency, and safe-to-spend only counts accounts held in it.
        </p>

        <label className="mb-1 block text-xs text-ink-muted" htmlFor="ledger-start">
          Opening balance month
        </label>
        <input
          id="ledger-start"
          type="month"
          className={`${inputClass} figure mb-1`}
          value={ledger.startMonth ?? ""}
          onChange={(event) =>
            void save(
              { startMonth: event.target.value === "" ? null : event.target.value },
              "Opening balance month updated.",
            )
          }
        />
        <p className="text-xs text-ink-muted">
          {ledger.startMonth
            ? `Account balances are treated as your position at the start of ${formatMonthLabel(
                ledger.startMonth,
              )}, and the runway projects forward from there.`
            : "Not set, so the runway starts from the month you are viewing. Set this to anchor your opening balances to a specific month."}
        </p>
      </div>
    </div>
  );
}
